import React, { useEffect, useMemo, useState } from "react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import { FilePdf, Plus, Trash, Check, X, Download, Clock } from "@phosphor-icons/react";

const API = `${window.location.origin}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
};

const todayIso = () => new Date().toISOString().slice(0, 10);

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Oczekuje", cls: "bg-amber-100 text-amber-700" },
    approved: { label: "Zaakceptowany", cls: "bg-emerald-100 text-emerald-700" },
    rejected: { label: "Odrzucony", cls: "bg-red-100 text-red-700" },
  };
  const item = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${item.cls}`}>
      {status === "approved" && <Check className="w-3 h-3" weight="bold" />}
      {status === "rejected" && <X className="w-3 h-3" weight="bold" />}
      {status === "pending" && <Clock className="w-3 h-3" weight="bold" />}
      {item.label}
    </span>
  );
}

// -----------------------------------------------------------------------
// EMPLOYEE FORM
// -----------------------------------------------------------------------
function WniosekForm({ onCreated, user }) {
  const isAdmin = user?.role === "admin";
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({
    employee_id: "",  // admin only
    miejscowosc: "",
    data: todayIso(),
    okres_od: "",
    okres_do: "",
    mode: "dni",
    ilosc: 0,
  });
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load employees list for admin
  useEffect(() => {
    if (!isAdmin) return;
    axios.get("/api/employees").then((res) => setEmployees(res.data || [])).catch(() => {});
  }, [isAdmin]);

  // Auto compute working days when okres changes and mode='dni'
  useEffect(() => {
    if (form.mode !== "dni" || !form.okres_od || !form.okres_do) return;
    let cancelled = false;
    setComputing(true);
    axios
      .get(`/api/wnioski-urlop/working-days?od=${form.okres_od}&do=${form.okres_do}`)
      .then((res) => {
        if (!cancelled) setForm((f) => ({ ...f, ilosc: res.data.working_days || 0 }));
      })
      .catch(() => {})
      .finally(() => !cancelled && setComputing(false));
    return () => { cancelled = true; };
  }, [form.okres_od, form.okres_do, form.mode]);

  const submit = async () => {
    setError(null);
    if (isAdmin && !form.employee_id) return setError("Wybierz pracownika");
    if (!form.miejscowosc.trim()) return setError("Wpisz miejscowość");
    if (!form.data) return setError("Wybierz datę wniosku");
    if (!form.okres_od || !form.okres_do) return setError("Wybierz okres urlopu");
    if (form.okres_do < form.okres_od) return setError("Data 'do' musi być późniejsza niż 'od'");
    if (!form.ilosc || Number(form.ilosc) <= 0) return setError("Podaj liczbę dni/godzin");

    setSaving(true);
    try {
      const body = { ...form, ilosc: Number(form.ilosc) };
      if (!isAdmin) delete body.employee_id;
      await axios.post("/api/wnioski-urlop", body);
      setForm({ employee_id: "", miejscowosc: "", data: todayIso(), okres_od: "", okres_do: "", mode: "dni", ilosc: 0 });
      onCreated?.();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-md p-5 shadow-sm space-y-4" data-testid="wniosek-form">
      <h3 className="text-lg font-bold text-foreground">Nowy wniosek o urlop</h3>

      {/* Employee selector (admin only) / Auto for employee */}
      {isAdmin ? (
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Pracownik</label>
          <select
            value={form.employee_id}
            onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md text-sm bg-white"
            data-testid="wniosek-employee-select"
          >
            <option value="">— wybierz —</option>
            {employees.map((e) => (
              <option key={e._id} value={e._id}>
                {(e.imie_nazwisko || e.name)} {e.imie_nazwisko ? `(${e.name})` : ""}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="bg-slate-50 border border-border rounded-md px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Pracownik</div>
          <div className="text-sm font-semibold text-foreground mt-0.5">{user?.name || "?"}</div>
        </div>
      )}

      {/* Miejscowość + data */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Miejscowość</label>
          <input
            type="text"
            value={form.miejscowosc}
            onChange={(e) => setForm({ ...form, miejscowosc: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
            placeholder="np. Warszawa"
            data-testid="wniosek-miejscowosc"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Data wniosku</label>
          <input
            type="date"
            value={form.data}
            onChange={(e) => setForm({ ...form, data: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-md text-sm"
            data-testid="wniosek-data"
          />
        </div>
      </div>

      {/* Okres od-do */}
      <div>
        <label className="block text-xs font-semibold text-muted-foreground mb-1">Na okres</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-muted-foreground mb-1">Od</div>
            <input
              type="date"
              value={form.okres_od}
              onChange={(e) => setForm({ ...form, okres_od: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md text-sm"
              data-testid="wniosek-od"
            />
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground mb-1">Do</div>
            <input
              type="date"
              value={form.okres_do}
              onChange={(e) => setForm({ ...form, okres_do: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md text-sm"
              data-testid="wniosek-do"
            />
          </div>
        </div>
      </div>

      {/* Mode toggle + ilosc */}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-muted-foreground mb-1">
            {form.mode === "dni" ? "Dni robocze (auto)" : "Ilość godzin"}
          </label>
          <input
            type="number"
            step={form.mode === "godziny" ? "0.5" : "1"}
            value={form.ilosc}
            onChange={(e) => setForm({ ...form, ilosc: e.target.value })}
            disabled={form.mode === "dni"}
            className={`w-full px-3 py-2 border border-border rounded-md text-sm ${form.mode === "dni" ? "bg-slate-100" : ""}`}
            data-testid="wniosek-ilosc"
          />
          {form.mode === "dni" && computing && (
            <div className="text-[10px] text-muted-foreground mt-1">Liczenie dni roboczych…</div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setForm({ ...form, mode: form.mode === "dni" ? "godziny" : "dni" })}
          className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border border-border rounded-md text-sm font-medium transition-colors"
          data-testid="wniosek-mode-toggle"
        >
          {form.mode === "dni" ? "→ godziny" : "→ dni"}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700" data-testid="wniosek-error">
          {error}
        </div>
      )}

      <button
        onClick={submit}
        disabled={saving}
        className="w-full sm:w-auto px-6 py-2.5 bg-[#2563EB] hover:bg-blue-700 text-white rounded-md text-sm font-semibold shadow-sm disabled:opacity-50"
        data-testid="wniosek-submit"
      >
        {saving ? "Wysyłanie..." : "Wyślij wniosek"}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------
// ADMIN APPROVAL MODAL
// -----------------------------------------------------------------------
function ApproveModal({ wniosek, onClose, onDone }) {
  const [d, setD] = useState({
    decyzja_okres_od: wniosek.okres_od,
    decyzja_okres_do: wniosek.okres_do,
    decyzja_mode: wniosek.mode,
    decyzja_ilosc: wniosek.ilosc,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/wnioski-urlop/${wniosek.id}/approve`, {
        ...d,
        decyzja_ilosc: Number(d.decyzja_ilosc),
      });
      onDone?.();
      onClose();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-2xl max-w-md w-full p-6 space-y-4" data-testid="approve-modal">
        <h3 className="text-lg font-bold">Decyzja pracodawcy</h3>
        <div className="text-sm text-muted-foreground">
          <div>Pracownik: <span className="font-semibold text-foreground">{wniosek.employee_name}</span></div>
          <div>Wniosek: {fmtDate(wniosek.okres_od)} – {fmtDate(wniosek.okres_do)} · {wniosek.ilosc} {wniosek.mode}</div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Okres od</label>
            <input type="date" value={d.decyzja_okres_od} onChange={(e) => setD({ ...d, decyzja_okres_od: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md text-sm" data-testid="approve-od" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Okres do</label>
            <input type="date" value={d.decyzja_okres_do} onChange={(e) => setD({ ...d, decyzja_okres_do: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md text-sm" data-testid="approve-do" />
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-muted-foreground mb-1">Wymiar ({d.decyzja_mode})</label>
            <input type="number" step="0.5" value={d.decyzja_ilosc} onChange={(e) => setD({ ...d, decyzja_ilosc: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md text-sm" data-testid="approve-ilosc" />
          </div>
          <button
            type="button"
            onClick={() => setD({ ...d, decyzja_mode: d.decyzja_mode === "dni" ? "godziny" : "dni" })}
            className="px-3 py-2 bg-slate-100 border border-border rounded-md text-sm"
          >
            {d.decyzja_mode === "dni" ? "→ godziny" : "→ dni"}
          </button>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border rounded-md">Anuluj</button>
          <button onClick={submit} disabled={saving}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-semibold disabled:opacity-50"
            data-testid="approve-confirm">
            {saving ? "..." : "Zatwierdź i wygeneruj PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// LIST + MAIN COMPONENT
// -----------------------------------------------------------------------
export default function WniosekUrlopTab({ mode = "employee" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const isAdmin = user?.role === "admin";

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/wnioski-urlop");
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const visible = useMemo(() => {
    if (mode === "do-podpisu") return items.filter((i) => i.status === "pending");
    return items;
  }, [items, mode]);

  const downloadPdf = async (id, name, od) => {
    try {
      const res = await axios.get(`/api/wnioski-urlop/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `wniosek_urlop_${name}_${od}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Nie udało się pobrać PDF: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteWniosek = async (id) => {
    if (!window.confirm("Usunąć wniosek?")) return;
    try {
      await axios.delete(`/api/wnioski-urlop/${id}`);
      fetchList();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Odrzucić wniosek?")) return;
    try {
      await axios.put(`/api/wnioski-urlop/${id}/reject`);
      fetchList();
    } catch (e) {
      alert("Błąd: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4" data-testid={`wnioski-urlop-${mode}`}>
      {/* Employee form — always visible (both employee and admin can create) */}
      {mode === "employee" && (
        <WniosekForm onCreated={fetchList} user={user} />
      )}

      {/* List */}
      <div className="bg-white border border-border rounded-md shadow-sm">
        <div className="px-5 py-3 border-b border-border">
          <h3 className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
            {mode === "do-podpisu" ? "Wnioski oczekujące" : (isAdmin ? "Wszystkie wnioski" : "Moje wnioski")}
          </h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Ładowanie...</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            {mode === "do-podpisu" ? "Brak wniosków oczekujących na podpis." : "Brak wniosków."}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {visible.map((w) => (
              <div key={w.id} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`wniosek-${w.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">{w.employee_name}</span>
                    <StatusBadge status={w.status} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {fmtDate(w.okres_od)} – {fmtDate(w.okres_do)}
                    <span className="mx-2">·</span>
                    {w.ilosc} {w.mode}
                    <span className="mx-2">·</span>
                    <span className="text-slate-500">{w.miejscowosc}, {fmtDate(w.data)}</span>
                  </div>
                  {w.status === "approved" && w.decyzja_okres_od && (
                    <div className="text-xs text-emerald-700 mt-1">
                      Decyzja: {fmtDate(w.decyzja_okres_od)} – {fmtDate(w.decyzja_okres_do)} · {w.decyzja_ilosc} {w.decyzja_mode}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isAdmin && w.status === "pending" && (
                    <>
                      <button onClick={() => setApproving(w)}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-semibold"
                        data-testid={`approve-${w.id}`}>
                        Zatwierdź
                      </button>
                      <button onClick={() => reject(w.id)}
                        className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-md text-xs font-semibold"
                        data-testid={`reject-${w.id}`}>
                        Odrzuć
                      </button>
                    </>
                  )}
                  {w.status === "approved" && (
                    <button onClick={() => downloadPdf(w.id, w.employee_name, w.okres_od)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border border-border hover:bg-slate-50 rounded-md text-xs font-semibold text-foreground"
                      data-testid={`pdf-${w.id}`}>
                      <Download className="w-3 h-3" /> PDF
                    </button>
                  )}
                  {(isAdmin || (w.status === "pending" && String(user?._id || user?.id) === String(w.employee_id))) && (
                    <button onClick={() => deleteWniosek(w.id)}
                      className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`delete-${w.id}`}>
                      <Trash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {approving && (
        <ApproveModal wniosek={approving} onClose={() => setApproving(null)} onDone={fetchList} />
      )}
    </div>
  );
}
